/**
 * Live transcription while a voice note is being recorded, using the
 * browser's own speech recogniser.
 *
 * This is the privacy-preserving half of the transcript story: Chrome and
 * Safari recognise speech without the audio ever reaching our server, and the
 * server-side provider is only asked when the browser cannot. Firefox has no
 * implementation at all, which is why every entry point here degrades to "no
 * transcript" rather than to an error.
 */

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event?: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function recognitionCtor(): SpeechRecognitionCtor | undefined {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

export function speechRecognitionAvailable(): boolean {
  return Boolean(recognitionCtor());
}

export interface SpeechCapture {
  /** Stops listening and resolves with whatever was recognised. */
  finish(): Promise<string>;
  /** Stops listening and throws the transcript away. */
  abort(): void;
}

/** How long to wait after stopping for the last result to arrive. */
const FLUSH_GRACE = 1200;

/**
 * Starts recognising speech, or returns null when the browser cannot.
 *
 * A null return is the normal path on Firefox and in the Expo app, and the
 * caller treats it as "send the voice note without a transcript".
 */
export function startSpeechCapture(lang = navigator.language || 'en-US'): SpeechCapture | null {
  const Ctor = recognitionCtor();
  if (!Ctor) return null;

  let recognition: SpeechRecognitionLike;
  try {
    recognition = new Ctor();
  } catch {
    return null;
  }

  let transcript = '';
  let ended = false;
  let onEnded: (() => void) | null = null;

  recognition.lang = lang;
  recognition.continuous = true;
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;

  recognition.onresult = (event) => {
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i];
      if (result?.isFinal) transcript += `${result[0].transcript} `;
    }
  };
  recognition.onerror = () => {
    // A recognition error costs the transcript, not the recording.
    ended = true;
    onEnded?.();
  };
  recognition.onend = () => {
    ended = true;
    onEnded?.();
  };

  try {
    recognition.start();
  } catch {
    return null;
  }

  return {
    finish() {
      return new Promise<string>((resolve) => {
        const settle = () => resolve(transcript.trim());
        if (ended) {
          settle();
          return;
        }
        onEnded = settle;
        // Never let a recogniser that refuses to end hold up the send.
        const timer = window.setTimeout(settle, FLUSH_GRACE);
        onEnded = () => {
          window.clearTimeout(timer);
          settle();
        };
        try {
          recognition.stop();
        } catch {
          window.clearTimeout(timer);
          settle();
        }
      });
    },
    abort() {
      onEnded = null;
      try {
        recognition.abort();
      } catch {
        // Already stopped; nothing to clean up.
      }
    },
  };
}

export interface LiveCaptions {
  stop(): void;
}

/**
 * Recognises speech continuously and hands over each finished sentence.
 *
 * The sibling of `startSpeechCapture` above, and different from it in the one
 * way that matters here: nothing is accumulated. A voice note wants the whole
 * transcript at the end; a subtitle wants each line the moment it is said, and
 * holding them back until the speaker stops talking would defeat the point.
 *
 * Only final results are handed over. Interim ones arrive several times a
 * second and change under you as the recogniser revises them — as a subtitle
 * that flickers, and as something to translate it would be a request per
 * revision. A sentence at a time is both readable and affordable.
 *
 * Chrome ends a continuous session on its own after a pause, so this restarts
 * itself until told to stop. It gives up for good on the errors that will not
 * get better — a refused microphone, a browser that has no recogniser — rather
 * than spinning on them.
 */
export function startLiveCaptions(
  lang: string,
  onSentence: (text: string) => void,
): LiveCaptions | null {
  const Ctor = recognitionCtor();
  if (!Ctor) return null;

  let stopped = false;
  let recognition: SpeechRecognitionLike | null = null;

  const run = (): boolean => {
    let next: SpeechRecognitionLike;
    try {
      next = new Ctor();
    } catch {
      return false;
    }
    next.lang = lang;
    next.continuous = true;
    next.interimResults = false;
    next.maxAlternatives = 1;

    next.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result?.isFinal ? result[0].transcript.trim() : '';
        if (text) onSentence(text);
      }
    };
    next.onerror = (event?: { error?: string }) => {
      // `no-speech` and `network` are the recogniser having a quiet moment;
      // a refused microphone is an answer, and retrying it would only ask
      // again forever.
      if (event?.error === 'not-allowed' || event?.error === 'service-not-allowed') {
        stopped = true;
      }
    };
    next.onend = () => {
      if (!stopped) run();
    };

    try {
      next.start();
    } catch {
      return false;
    }
    recognition = next;
    return true;
  };

  if (!run()) return null;

  return {
    stop() {
      stopped = true;
      try {
        recognition?.abort();
      } catch {
        // Already gone; nothing to stop.
      }
    },
  };
}
